import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@betstarters/ui'
import { TriangleAlert } from 'lucide-react'
import { api } from '../api'
import {
  CatalogToolbar,
  ErrorBanner,
  PageHeading,
  Pagination,
  StatusBadge,
} from '../components/catalog'
import { Combobox } from '../components/controls'
import { useSettingTemplates } from '../hooks/useReference'

export interface RuleSetting {
  settingTypeId: number
  settingType: string
  templateTypeId: number
  templateType: string
  settingGroupId: number
  settingGroupType: string
  groupTemplateTypeId: number
  displayOrder: number
  networkTypeCount: number
  thresholdCount: number
  isDisabledByPlatform: boolean
  creationDate: string
  lastModificationDate: string | null
}

interface RuleSettingPage {
  settingTypes: RuleSetting[]
  total: number
  offset: number
  limit: number
}

const PAGE_SIZE = 50

/**
 * RuleSettingsPage is the middle of the rules engine —
 * [Catalog].[Setting.SettingType], 454 rows.
 *
 * Names are not unique anywhere (CRYPTO.ETH.DEPOSIT exists twice inside one
 * group), so the id is shown and the name is not treated as an identifier. And
 * where a setting's template disagrees with its group's — two production rows do
 * — the row says so instead of rendering a group that belongs elsewhere.
 */
export function RuleSettingsPage() {
  const { t } = useTranslation('app')
  const { templates } = useSettingTemplates()

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [templateFilter, setTemplateFilter] = useState('')
  const [includeDisabled, setIncludeDisabled] = useState(true)
  const [offset, setOffset] = useState(0)

  const params = {
    search,
    includeDisabled: String(includeDisabled),
    offset,
    limit: PAGE_SIZE,
    ...(templateFilter ? { templateTypeId: templateFilter } : {}),
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['settings', params],
    queryFn: async (): Promise<RuleSettingPage> => {
      const res = await api.get('/settings', { params })
      return res.data as RuleSettingPage
    },
  })

  const settings = data?.settingTypes ?? []
  const total = data?.total ?? 0
  const templateItems = templates.map((tpl) => ({ value: String(tpl.id), label: tpl.name }))

  return (
    <div>
      <PageHeading title={t('ruleSettings.title')} subtitle={t('ruleSettings.subtitle')} />

      <CatalogToolbar
        searchInput={searchInput}
        onSearchInput={setSearchInput}
        onSubmitSearch={() => {
          setSearch(searchInput)
          setOffset(0)
        }}
        searchPlaceholder={t('ruleSettings.searchPlaceholder')}
        includeDisabled={includeDisabled}
        onToggleDisabled={() => {
          setIncludeDisabled(!includeDisabled)
          setOffset(0)
        }}
        filtered={search !== '' || !includeDisabled || templateFilter !== ''}
        onClear={() => {
          setSearch('')
          setSearchInput('')
          setTemplateFilter('')
          setIncludeDisabled(true)
          setOffset(0)
        }}
      >
        <Combobox
          value={templateFilter}
          onValueChange={(v) => {
            setTemplateFilter(v)
            setOffset(0)
          }}
          items={templateItems}
          placeholder={t('ruleSettings.allTemplates')}
          className="w-56"
        />
      </CatalogToolbar>

      <ErrorBanner message={error ? t('ruleSettings.errors.load') : ''} />

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-16">{t('ruleSettings.columns.id')}</TableHead>
                  <TableHead>{t('ruleSettings.columns.name')}</TableHead>
                  <TableHead className="w-40">{t('ruleSettings.columns.template')}</TableHead>
                  <TableHead className="w-64">{t('ruleSettings.columns.group')}</TableHead>
                  <TableHead className="w-44">{t('ruleSettings.columns.links')}</TableHead>
                  <TableHead className="w-32">{t('common.columns.status')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {settings.map((row) => (
                  <TableRow key={row.settingTypeId}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {row.settingTypeId}
                    </TableCell>
                    <TableCell className="font-medium">
                      <Link to={`/rules/settings/${row.settingTypeId}`} className="hover:underline">
                        {row.settingType}
                      </Link>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {row.templateType || `#${row.templateTypeId}`}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <span className="flex items-center gap-1.5">
                        {row.settingGroupType || `#${row.settingGroupId}`}
                        {row.groupTemplateTypeId !== 0 &&
                          row.groupTemplateTypeId !== row.templateTypeId && (
                            <TriangleAlert
                              className="size-3.5 text-amber-600"
                              // Two production rows sit in a group of another
                              // template; the page says so rather than hiding it.
                              aria-label={t('ruleSettings.mismatch')}
                            />
                          )}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {row.networkTypeCount > 0 && (
                          <Badge variant="outline">
                            {t('ruleSettings.counts.networks', { count: row.networkTypeCount })}
                          </Badge>
                        )}
                        {row.thresholdCount > 0 && (
                          <Badge variant="outline">
                            {t('ruleSettings.counts.thresholds', { count: row.thresholdCount })}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge disabled={row.isDisabledByPlatform} />
                    </TableCell>
                  </TableRow>
                ))}
                {settings.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                      {t('ruleSettings.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <Pagination
            total={total}
            offset={offset}
            pageSize={PAGE_SIZE}
            onOffset={setOffset}
            label={t('ruleSettings.total', { count: total })}
          />
        </>
      )}
    </div>
  )
}
